/**
 * 路由系统 - 使用 Navigo
 * 支持乐观 UI 路由跳转，先显示页面再验证认证
 */

import Navigo from 'navigo';
import { ErrorType } from './error-handler';
import { toast } from '@/components/toast';

export type Route = 'home' | 'video-list' | 'search' | 'search-entry' | 'player' | 'login' | 'users' | 'change-password';

// ==================== 状态管理 ====================

interface AuthState {
  isAuthenticated: boolean;
  isVIP: boolean;
  isAdmin: boolean;
  forcePasswordChange: boolean;
  user?: {
    id: string;
    username: string;
  };
}

let currentAuthState: AuthState = {
  isAuthenticated: false,
  isVIP: false,
  isAdmin: false,
  forcePasswordChange: false,
};

// ==================== 路由配置 ====================

interface RouteConfig {
  requireAuth: boolean;
  requireVIP?: boolean;
  requireAdmin?: boolean;
}

const ROUTE_CONFIGS: Record<Route, RouteConfig> = {
  login: { requireAuth: false },
  home: { requireAuth: true },
  'video-list': { requireAuth: true },
  search: { requireAuth: true },
  'search-entry': { requireAuth: true },
  player: { requireAuth: true },
  users: { requireAuth: true, requireAdmin: true },
  'change-password': { requireAuth: true },
};

// ==================== 创建路由实例 ====================

export const router = new Navigo('/', {
  hash: false,
  linksSelector: 'a[href^="/"]', // 自动拦截所有内部链接
});

// ==================== 认证相关方法 ====================

/**
 * 更新认证状态
 */
export function setAuthState(state: Partial<AuthState>): void {
  currentAuthState = { ...currentAuthState, ...state };
}

/**
 * 获取当前认证状态
 */
export function getAuthState(): AuthState {
  return { ...currentAuthState };
}

/**
 * 检查是否已登录（前端状态）
 */
export function isAuthenticated(): boolean {
  return currentAuthState.isAuthenticated;
}

/**
 * 检查是否是 VIP
 */
export function isVIP(): boolean {
  return currentAuthState.isVIP;
}

/**
 * 检查是否是管理员
 */
export function isAdmin(): boolean {
  return currentAuthState.isAdmin;
}

/**
 * 检查是否需要强制修改密码
 */
export function needForcePasswordChange(): boolean {
  return currentAuthState.forcePasswordChange;
}

/**
 * 保存认证信息到本地
 */
export function saveAuthToStorage(): void {
  if (currentAuthState.isAuthenticated) {
    sessionStorage.setItem('auth_state', JSON.stringify({
      isAuthenticated: true,
      isVIP: currentAuthState.isVIP,
      isAdmin: currentAuthState.isAdmin,
      forcePasswordChange: currentAuthState.forcePasswordChange,
      user: currentAuthState.user,
    }));
  } else {
    sessionStorage.removeItem('auth_state');
  }
}

/**
 * 从本地恢复认证信息
 */
function restoreAuthFromStorage(): void {
  const saved = sessionStorage.getItem('auth_state');
  if (saved) {
    try {
      const state = JSON.parse(saved);
      currentAuthState = state;
    } catch {
      // 忽略解析错误
    }
  }
}

/**
 * 登出
 */
export function logout(): void {
  currentAuthState = {
    isAuthenticated: false,
    isVIP: false,
    isAdmin: false,
    forcePasswordChange: false,
  };
  sessionStorage.removeItem('auth_state');
  router.navigate('/login');
}

// ==================== 全局路由守卫 ====================

// 防止重复检查的标志
let isCheckingAuth = false;
let lastCheckTime = 0;
const CHECK_THROTTLE = 30000; // 30秒内不重复检查

/**
 * 检查认证状态（非阻塞，后台验证）
 * 返回 Promise，但不阻塞页面跳转
 */
async function checkAuthenticationInBackground(): Promise<void> {
  // 防止重复检查
  const now = Date.now();
  if (isCheckingAuth || (now - lastCheckTime < CHECK_THROTTLE)) {
    return;
  }

  isCheckingAuth = true;
  lastCheckTime = now;

  try {
    const { checkSession } = await import('./auth');
    const result = await checkSession();

    if (result.success && result.data.user) {
      // 认证成功，更新状态
      currentAuthState = {
        isAuthenticated: true,
        isVIP: result.data.user.isVIP || false,
        isAdmin: result.data.user.isAdmin || false,
        forcePasswordChange: result.data.user.forcePasswordChange || false,
        user: result.data.user,
      };
      saveAuthToStorage();
      return;
    }

    // 处理认证失败
    if (!result.success) {
      const error = result.error;

      // 网络错误或超时错误 = 后端不可用
      if (error.type === ErrorType.NETWORK_ERROR || error.type === ErrorType.TIMEOUT_ERROR) {
        // 如果有缓存的认证状态，静默降级
        if (!currentAuthState.isAuthenticated) {
          // 没有缓存状态，显示提示
          toast.warning('网络连接失败', '正在使用离线模式');
        }
        console.warn('[认证检查] 后端不可用，使用缓存状态');
        return;
      }

      // 认证错误（未登录、过期），清除本地状态并跳转登录
      if (error.type === ErrorType.AUTH_ERROR) {
        currentAuthState = {
          isAuthenticated: false,
          isVIP: false,
          isAdmin: false,
          forcePasswordChange: false,
        };
        sessionStorage.removeItem('auth_state');

        // 保存目标页面，登录后跳回
        const currentPath = window.location.pathname;
        if (currentPath !== '/login') {
          sessionStorage.setItem('redirect_after_login', currentPath);
        }

        // 显示提示并跳转
        toast.error('登录已过期', '请重新登录');
        setTimeout(() => {
          router.navigate('/login');
        }, 500);
        return;
      }
    }

  } catch (error) {
    console.error('认证检查失败:', error);
    // 静默失败，不影响用户体验
  } finally {
    isCheckingAuth = false;
  }
}

/**
 * 同步检查认证状态（用于关键操作）
 * 导出以供外部使用
 */
export async function checkAuthenticationSync(): Promise<boolean> {
  const now = Date.now();
  if (isCheckingAuth || (now - lastCheckTime < CHECK_THROTTLE)) {
    return currentAuthState.isAuthenticated;
  }

  isCheckingAuth = true;
  lastCheckTime = now;

  try {
    const { checkSession } = await import('./auth');
    const result = await checkSession();

    if (result.success && result.data.user) {
      currentAuthState = {
        isAuthenticated: true,
        isVIP: result.data.user.isVIP || false,
        isAdmin: result.data.user.isAdmin || false,
        forcePasswordChange: result.data.user.forcePasswordChange || false,
        user: result.data.user,
      };
      saveAuthToStorage();
      return true;
    }

    if (!result.success) {
      const error = result.error;
      if (error.type === ErrorType.NETWORK_ERROR || error.type === ErrorType.TIMEOUT_ERROR) {
        return currentAuthState.isAuthenticated; // 降级
      }
      if (error.type === ErrorType.AUTH_ERROR) {
        currentAuthState = { isAuthenticated: false, isVIP: false, isAdmin: false, forcePasswordChange: false };
        sessionStorage.removeItem('auth_state');
        return false;
      }
    }

    return false;

  } catch (error) {
    console.error('认证检查失败:', error);
    return false;
  } finally {
    isCheckingAuth = false;
  }
}

/**
 * 获取路由配置
 */
function getRouteConfig(url: string): { route: Route; config: RouteConfig } | null {
  // 标准化 URL，确保有前导斜杠
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

  // 解析 URL 匹配路由
  if (normalizedUrl === '/' || normalizedUrl === '/index.html') {
    return { route: 'home', config: ROUTE_CONFIGS.home };
  }

  if (normalizedUrl.startsWith('/video/')) {
    return { route: 'video-list', config: ROUTE_CONFIGS['video-list'] };
  }

  if (normalizedUrl === '/search-entry') {
    return { route: 'search-entry', config: ROUTE_CONFIGS['search-entry'] };
  }

  if (normalizedUrl.startsWith('/search')) {
    return { route: 'search', config: ROUTE_CONFIGS.search };
  }

  if (normalizedUrl.startsWith('/player')) {
    return { route: 'player', config: ROUTE_CONFIGS.player };
  }

  if (normalizedUrl === '/login') {
    return { route: 'login', config: ROUTE_CONFIGS.login };
  }

  if (normalizedUrl === '/users') {
    return { route: 'users', config: ROUTE_CONFIGS.users };
  }

  if (normalizedUrl === '/change-password') {
    return { route: 'change-password', config: ROUTE_CONFIGS['change-password'] };
  }

  // 默认需要登录
  return { route: 'home', config: ROUTE_CONFIGS.home };
}

// 注册全局前置守卫（乐观模式，不阻塞）
router.hooks({
  before: (done, match) => {
    const url = match.url;
    console.log('[路由守卫] 检查路由:', url);
    const routeInfo = getRouteConfig(url);

    if (!routeInfo) {
      done();
      return;
    }

    const { route, config } = routeInfo;

    // 如果是登录页面，直接放行
    if (route === 'login') {
      done();
      return;
    }

    // 同步检查：如果明确知道没登录，立即跳转
    if (config.requireAuth && !currentAuthState.isAuthenticated && !sessionStorage.getItem('auth_state')) {
      console.log('[路由守卫] 未登录，跳转到登录页面');
      sessionStorage.setItem('redirect_after_login', url);
      done(false);
      router.navigate('/login');
      return;
    }

    // 检查管理员权限（同步，必须有缓存状态）
    if (config.requireAdmin && !currentAuthState.isAdmin) {
      done(false);
      router.navigate('/');
      return;
    }

    // 检查强制修改密码（同步）
    if (currentAuthState.forcePasswordChange && route !== 'change-password') {
      done(false);
      router.navigate('/change-password');
      return;
    }

    // 放行，同时在后台验证认证状态
    done();
    checkAuthenticationInBackground();
  },

  // after 钩子：页面加载完成后再次验证
  after: () => {
    // 页面加载完成后，确保认证状态是最新的
    checkAuthenticationInBackground();
  }
});

// ==================== 路由定义 ====================

function renderPage(html: string, init: () => void): void {
  const app = document.getElementById('app');
  if (!app) {
    console.error('找不到 #app 元素');
    return;
  }

  app.innerHTML = html;
  init();
}

// 注册路由
router
  .on('/', async () => {
    const { renderHomePage, initHomePage } = await import('@/pages/home');
    renderPage(renderHomePage(), initHomePage);
  })
  .on('/login', async () => {
    const { renderLoginPage, initLoginPage } = await import('@/pages/login');
    renderPage(renderLoginPage(), initLoginPage);
  })
  .on('/video/:tag', async (params: any) => {
    const url = new URL(window.location.href);
    const page = url.searchParams.get('page') || '1';
    const { renderVideoListPage, initVideoListPage } = await import('@/pages/video-list');
    renderPage(renderVideoListPage(), () => initVideoListPage({ tag: params?.data?.tag || '', page }));
  })
  .on('/search', async (match: any) => {
    // Navigo 的 match 对象提供 queryString 属性
    const queryString = match?.queryString || '';
    const urlParams = new URLSearchParams(queryString);
    const query = urlParams.get('q') || '';
    const page = urlParams.get('page') || '1';
    const { renderSearchPage, initSearchPage } = await import('@/pages/search');
    renderPage(renderSearchPage(), () => initSearchPage({ q: query, page }));
  })
  .on('/search-entry', async () => {
    const { renderSearchEntryPage, initSearchEntryPage } = await import('@/pages/search-entry');
    renderPage(renderSearchEntryPage(), initSearchEntryPage);
  })
  .on('/player', async () => {
    // player 页面特殊处理，在新窗口打开
    const url = new URL(window.location.href);
    const videoUrl = url.searchParams.get('v');

    if (videoUrl) {
      window.open(`/player.html?v=${encodeURIComponent(videoUrl)}`, '_blank');
    }

    // 返回上一页或首页
    router.navigate('/');
  })
  .on('/users', async () => {
    const { renderUsersPage, initUsersPage } = await import('@/pages/users');
    renderPage(renderUsersPage(), initUsersPage);
  })
  .on('/change-password', async () => {
    const { renderChangePasswordPage, initChangePasswordPage } = await import('@/pages/change-password');
    renderPage(renderChangePasswordPage(), initChangePasswordPage);
  });

// ==================== 导航辅助函数 ====================

/**
 * 导航到指定路由
 */
export function navigate(
  route: Route,
  params: Record<string, string> = {}
): void {
  switch (route) {
    case 'home':
      router.navigate('/');
      break;

    case 'login':
      router.navigate('/login');
      break;

    case 'video-list':
      const tag = params.tag || '';
      const page = params.page || '1';
      let videoPath = `/video/${tag}`;
      if (page !== '1') {
        videoPath += `?page=${page}`;
      }
      router.navigate(videoPath);
      break;

    case 'search':
      const query = params.q || '';
      const searchPage = params.page || '1';
      let searchPath = `/search?q=${encodeURIComponent(query)}`;
      if (searchPage !== '1') {
        searchPath += `&page=${searchPage}`;
      }
      router.navigate(searchPath);
      break;

    case 'player':
      // 播放器独立窗口打开
      const videoUrl = params.url || '';
      if (videoUrl) {
        window.open(`/player.html?v=${encodeURIComponent(videoUrl)}`, '_blank');
      }
      break;

    case 'users':
      router.navigate('/users');
      break;

    case 'change-password':
      router.navigate('/change-password');
      break;
  }
}

// ==================== 初始化 ====================

/**
 * 初始化路由系统
 */
export async function initRouter(): Promise<void> {
  // 尝试恢复认证状态
  restoreAuthFromStorage();

  // 解析当前路由
  router.resolve();
}

/**
 * 页面加载后的初始化
 */
export function onRouterReady(callback: () => void): void {
  router.on('after', callback);
}
